import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * The quiet horizontal band that sits above a table or a page section and
 * states one fact about what you are looking at — a scope, a status, a source,
 * a constraint — usually with one action on the right.
 *
 * ---
 *
 * WHY THIS FILE EXISTS, because the history is the point.
 *
 * `DECISION_LOG.md` D10 (2026-08-24) recorded that this exact recipe was
 * copy-pasted across five sites with no primitive, and that ONE had already
 * drifted. The decision was correct and was never executed. Twenty-four hours
 * later an audit found the recipe at **ten** sites, and the drifted one still
 * drifted:
 *
 *     canonical   rounded-xl border border-border/50 bg-card/30 px-4 py-2.5
 *     drifted     rounded-md border border-border/60 bg-card/40 px-4 py-3
 *
 * Four values, all slightly different. Nobody ignored the design system; there
 * was simply nothing to import, so ten implementers re-derived the same shape
 * from memory and landed in ten nearly-identical places. Viewed one at a time
 * that is invisible. Viewed across four sibling surfaces in one sitting — which
 * is how a founder actually encounters a feature — it reads as "designing
 * without a design system at all", which is the verdict that produced this
 * file.
 *
 * The generalisable rule, now that it has cost us twice: **a missing primitive
 * is a finding, not a licence to inline.** Recording that one should exist is
 * not the same as making one exist, and the gap between those two is measured
 * in duplicate implementations per day.
 *
 * ---
 *
 * The bare `border` is load-bearing. `border-border/50` is a COLOUR utility and
 * sets no width, so dropping the bare `border` renders the strip borderless —
 * invisible in light mode, where `bg-card/30` barely separates from the page.
 * That is why it is stated here once instead of at every call site.
 */
const CONTEXT_STRIP_BASE = 'rounded-xl border border-border/50 bg-card/30';

const CONTEXT_STRIP_PADDING = {
  /** The default. Matches all ten prior inline copies. */
  default: 'px-4 py-2.5',
  /** For a strip carrying two stacked lines rather than one. */
  roomy: 'p-3',
} as const;

const CONTEXT_STRIP_TONE = {
  neutral: '',
  /** A strip that states a problem a person has to decide on. Pair it with one action. */
  warning: 'border-warning/30 bg-warning/10',
} as const;

export interface ContextStripProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * `warning` tints the band for the one case a quiet strip is not enough: a problem that needs a
   * decision. `DecisionBanner` is the component that uses it; a strip that only states a scope stays
   * `neutral`.
   */
  tone?: keyof typeof CONTEXT_STRIP_TONE;
  /**
   * `roomy` exists so a two-line strip does not have to re-declare padding at
   * the call site and drift by a quarter step — which is exactly how the
   * `py-3` variant got loose in the first place.
   */
  padding?: keyof typeof CONTEXT_STRIP_PADDING;
  /**
   * Lays the strip out as `content | action` with the action pinned right.
   * Most strips want this; it is opt-in so a strip holding only prose stays a
   * plain block.
   */
  split?: boolean;
  /**
   * `section` for a strip that is a labelled landmark — several call sites
   * carry an `aria-label` naming what the strip is about, and an `aria-label`
   * on a bare `div` names nothing. A variant, not a second component: rule 33.
   */
  as?: 'div' | 'section';
  asChild?: never;
}

export function ContextStrip({
  tone = 'neutral',
  padding = 'default',
  split = false,
  as: Element = 'div',
  className,
  children,
  ...props
}: ContextStripProps): JSX.Element {
  return (
    <Element
      className={cn(
        CONTEXT_STRIP_BASE,
        CONTEXT_STRIP_PADDING[padding],
        CONTEXT_STRIP_TONE[tone],
        split && 'flex flex-wrap items-center justify-between gap-3',
        className,
      )}
      {...props}
    >
      {children}
    </Element>
  );
}

/**
 * Exported for the handful of places that need the classes without the element
 * — a `<section>`, a `<button>`, a row that is itself a link. Prefer the
 * component; reach for this only when the tag must differ.
 */
export const CONTEXT_STRIP_CLASS = cn(CONTEXT_STRIP_BASE, CONTEXT_STRIP_PADDING.default);
